import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { useHistory } from "react-router-dom";
import { Typography, Button, TextField, Paper, Chip, IconButton, LinearProgress } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import PhoneIcon from "@material-ui/icons/Phone";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import { format } from "date-fns";
import { Can } from "../../components/Can";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ShowChartIcon from "@material-ui/icons/ShowChart";

import {
  KoraxPage,
  KoraxPageHeader,
  KoraxButton,
  KoraxStatsGrid,
  KoraxStatCard
} from "../../components/KoraxUI";

const useStyles = makeStyles((theme) => ({
  hero: {
    position: "relative",
    flexShrink: 0,
    minWidth: 0,
    padding: "15px 17px",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "nowrap",

    overflow: "hidden",
    borderRadius: 16,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(148,163,184,.16)"
        : "rgba(21,94,239,.11)"
    }`,

    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg,#101828,#16243A)"
        : "linear-gradient(135deg,#FFFFFF 0%,#EEF5FF 100%)",

    boxShadow:
      theme.palette.type === "dark"
        ? "0 8px 24px rgba(0,0,0,.18)"
        : "0 8px 24px rgba(15,23,42,.055)",

    [theme.breakpoints.down("sm")]: {
      padding: "14px",
      gap: 13,
      alignItems: "stretch",
      flexDirection: "column"
    }
  },
  heroBgCircle1: {
    position: "absolute", right: -40, top: -40, width: 220, height: 220,
    borderRadius: "50%", pointerEvents: "none",
    background: theme.palette.type === "dark"
      ? "radial-gradient(circle, rgba(0,140,255,0.08) 0%, transparent 70%)"
      : "radial-gradient(circle, rgba(0,102,255,0.07) 0%, transparent 70%)",
  },
  heroBgCircle2: {
    position: "absolute", right: 160, bottom: -60, width: 160, height: 160,
    borderRadius: "50%", pointerEvents: "none",
    background: theme.palette.type === "dark"
      ? "radial-gradient(circle, rgba(0,180,255,0.06) 0%, transparent 70%)"
      : "radial-gradient(circle, rgba(0,153,255,0.06) 0%, transparent 70%)",
  },
  heroLeft: {
    position: "relative",
    zIndex: 1,
    minWidth: 0,
    flex: 1,

    display: "flex",
    alignItems: "center",
    gap: 13,

    [theme.breakpoints.down("sm")]: {
      alignItems: "flex-start",
      gap: 11
    }
  },
  heroAvatar: {
    flexShrink: 0,
    width: 48,
    height: 48,

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: 14,
    color: "#FFFFFF",
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    boxShadow: "0 9px 20px rgba(0,102,255,.20)",

    fontSize: 21,

    [theme.breakpoints.down("sm")]: {
      width: 44,
      height: 44,
      borderRadius: 13,
      fontSize: 19
    }
  },
  heroTextWrap: {
    minWidth: 0,
    flex: 1
  },
  heroBadge: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: theme.palette.type === "dark" ? "rgba(0,208,132,0.15)" : "rgba(0,168,107,0.1)",
    border: "1px solid rgba(0,208,132,0.3)", borderRadius: 20, padding: "2px 10px",
    fontSize: "0.68rem", fontWeight: 700, color: "#00A86B", marginBottom: 6,
  },
  heroBadgeDot: {
    width: 6, height: 6, borderRadius: "50%", background: "#00D084",
    boxShadow: "0 0 6px rgba(0,208,132,0.6)",
  },
  heroTitle: {
    color:
      theme.palette.type === "dark"
        ? "#FFFFFF"
        : "#0A1628",

    fontSize: "1.32rem",
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: "-.035em",

    [theme.breakpoints.down("sm")]: {
      fontSize: "1.18rem",
      lineHeight: 1.18
    }
  },
  heroSubtitle: {
    maxWidth: 600,
    marginTop: 4,

    color:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.58)"
        : "#667085",

    fontSize: ".77rem",
    lineHeight: 1.4,

    [theme.breakpoints.down("sm")]: {
      marginTop: 5,
      fontSize: ".74rem",
      lineHeight: 1.38
    }
  },
  heroMetrics: {
    position: "relative",
    zIndex: 1,
    flexShrink: 0,

    minWidth: 430,
    display: "grid",
    gridTemplateColumns: "repeat(4,minmax(92px,1fr))",
    gap: 7,

    [theme.breakpoints.down("md")]: {
      minWidth: 350,
      gridTemplateColumns: "repeat(2,minmax(110px,1fr))"
    },

    [theme.breakpoints.down("sm")]: {
      minWidth: 0,
      width: "100%",
      gridTemplateColumns: "repeat(2,minmax(0,1fr))",
      gap: 7
    }
  },
  metricCard: {
    minWidth: 0,
    minHeight: 58,
    padding: "9px 10px",

    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",

    textAlign: "center",
    borderRadius: 12,

    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.065)"
        : "#FFFFFF",

    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,.09)"
        : "1px solid rgba(15,23,42,.055)",

    boxShadow: "0 3px 10px rgba(15,23,42,.04)",

    [theme.breakpoints.down("sm")]: {
      minHeight: 62,
      padding: "9px 7px"
    }
  },
  metricNum: {
    color:
      theme.palette.type === "dark"
        ? "#FFFFFF"
        : "#0A1628",

    fontSize: "1.28rem",
    fontWeight: 800,
    lineHeight: 1,

    [theme.breakpoints.down("sm")]: {
      fontSize: "1.38rem"
    }
  },
  metricLabel: {
    marginTop: 5,

    color:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.46)"
        : "#667085",

    fontSize: ".61rem",
    fontWeight: 650,
    lineHeight: 1.15,
    letterSpacing: ".035em",
    textTransform: "uppercase"
  },
  metricTrend: {
    fontSize: "0.65rem", fontWeight: 700, marginTop: 4,
    display: "inline-flex", alignItems: "center", gap: 2,
    background: "rgba(0,208,132,0.1)", color: "#00A86B",
    border: "1px solid rgba(0,208,132,0.2)", borderRadius: 6, padding: "1px 6px",
  },
  filterBar: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    flexShrink: 0,

    padding: "10px 12px",
    borderRadius: 13,

    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 5px 16px rgba(15,23,42,.045)",

    [theme.breakpoints.down("sm")]: {
      position: "relative",
      top: "auto",
      padding: "10px",
      borderRadius: 14
    }
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",

    [theme.breakpoints.down("sm")]: {
      display: "grid",
      gridTemplateColumns: "repeat(2,minmax(0,1fr))",
      gap: 8,

      "& .MuiTextField-root": {
        width: "100%"
      },

      "& button": {
        width: "100%",
        minWidth: 0
      }
    }
  },
  dateField: {
    minWidth: 145,

    "& .MuiOutlinedInput-root": {
      height: 36,
      borderRadius: 9,
      fontSize: ".74rem",

      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#0066FF"
      }
    },

    "& .MuiInputLabel-root": {
      fontSize: ".72rem"
    },

    [theme.breakpoints.down("sm")]: {
      minWidth: 0
    }
  },
  searchBtn: {
    minHeight: 36,
    padding: "5px 17px",

    borderRadius: 9,
    color: "#FFFFFF",
    background: "linear-gradient(135deg,#0066FF,#0099FF)",
    boxShadow: "0 5px 13px rgba(0,102,255,.20)",

    fontSize: ".75rem",
    fontWeight: 700,
    textTransform: "none",

    "&:hover": {
      background: "linear-gradient(135deg,#0052CC,#007ACC)"
    }
  },
  addColBtn: {
    minHeight: 36,
    padding: "5px 14px",

    borderRadius: 9,
    border: "1px solid rgba(0,102,255,.27)",
    color: "#0066FF",

    fontSize: ".74rem",
    fontWeight: 700,
    textTransform: "none",

    "&:hover": {
      background: "rgba(0,102,255,.045)"
    }
  },
  filterInfo: {
    marginTop: 7,
    display: "flex",
    alignItems: "center",
    gap: 5,
    flexWrap: "wrap"
  },
  infoChip: {
    height: 19,

    color: "#0066FF",
    background: "rgba(0,102,255,.06)",
    border: "1px solid rgba(0,102,255,.13)",

    fontSize: ".61rem",
    fontWeight: 650,

    "& .MuiChip-label": {
      paddingLeft: 7,
      paddingRight: 7
    }
  },
  boardNavigator: {
    position: "sticky",
    top: 0,
    zIndex: 30,

    flexShrink: 0,
    padding: "8px 11px",

    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,

    background:
      theme.palette.type === "dark"
        ? "rgba(15,23,42,.96)"
        : "rgba(255,255,255,.97)",

    boxShadow: "0 5px 16px rgba(15,23,42,.06)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",

    [theme.breakpoints.down("sm")]: {
      padding: "7px 9px",
      borderRadius: 11
    }
  },

  boardNavigatorHeader: {
    marginBottom: 5,

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8
  },

  boardNavigatorTitle: {
    color: theme.palette.text.primary,
    fontSize: ".68rem",
    fontWeight: 700
  },

  boardNavigatorMeta: {
    color: theme.palette.text.secondary,
    fontSize: ".61rem",
    fontWeight: 600
  },

  boardNavigatorControl: {
    display: "flex",
    alignItems: "center",
    gap: 8
  },

  boardNavigatorArrow: {
    flexShrink: 0,

    width: 22,
    height: 22,

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: 7,

    background:
      theme.palette.type === "dark"
        ? "rgba(148,163,184,.10)"
        : "rgba(21,94,239,.06)",

    color: "#155EEF",
    fontSize: 17,
    fontWeight: 700,
    lineHeight: 1
  },

  boardNavigatorRange: {
    flex: 1,
    height: 5,

    appearance: "none",
    WebkitAppearance: "none",

    borderRadius: 999,
    outline: "none",

    background:
      theme.palette.type === "dark"
        ? "rgba(148,163,184,.18)"
        : "rgba(15,23,42,.10)",

    cursor: "pointer",

    "&::-webkit-slider-thumb": {
      width: 34,
      height: 13,

      appearance: "none",
      WebkitAppearance: "none",

      border: "2px solid #FFFFFF",
      borderRadius: 999,

      background:
        "linear-gradient(90deg,#155EEF,#0BA5EC)",

      boxShadow:
        "0 3px 9px rgba(21,94,239,.28)",

      cursor: "grab"
    },

    "&::-webkit-slider-thumb:active": {
      cursor: "grabbing"
    },

    "&::-moz-range-thumb": {
      width: 34,
      height: 13,

      border: "2px solid #FFFFFF",
      borderRadius: 999,

      background:
        "linear-gradient(90deg,#155EEF,#0BA5EC)",

      boxShadow:
        "0 3px 9px rgba(21,94,239,.28)",

      cursor: "grab"
    }
  },

  boardShell: {
    position: "relative",
    flex: "0 0 auto",
    minWidth: 0,
    minHeight: 360,

    display: "flex",
    flexDirection: "column",
    alignSelf: "stretch",

    overflow: "hidden",
    borderRadius: 14,

    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 14,
      width: 24,
      zIndex: 4,
      pointerEvents: "none",
      background:
        theme.palette.type === "dark"
          ? "linear-gradient(90deg,transparent,#0B1220)"
          : "linear-gradient(90deg,transparent,#F4F7FB)"
    },

    [theme.breakpoints.down("sm")]: {
      overflow: "visible",

      "&::after": {
        display: "none"
      }
    }
  },
  boardScroll: {
    flex: "0 0 auto",
    minWidth: 0,

    display: "flex",
    alignItems: "flex-start",
    gap: 11,

    padding: "3px 22px 18px 1px",
    overflowX: "auto",
    overflowY: "visible",

    scrollSnapType: "x proximity",
    scrollBehavior: "smooth",
    WebkitOverflowScrolling: "touch",
    overscrollBehaviorX: "contain",

    ...theme.scrollbarStyles,

    
    scrollbarWidth: "none",
    msOverflowStyle: "none",

    "&::-webkit-scrollbar": {
      display: "none"
    },
[theme.breakpoints.down("sm")]: {
      gap: 10,
      padding: "3px 1px 18px",
      scrollSnapType: "x mandatory",
      scrollPaddingLeft: 1,
      scrollPaddingRight: 1
    }
  },
  column: {
    position: "relative",
    flexShrink: 0,

    minWidth: 286,
    maxWidth: 286,

    display: "flex",
    flexDirection: "column",

    height: "auto",
    maxHeight: "none",
    overflow: "visible",

    scrollSnapAlign: "start",
    scrollSnapStop: "normal",

    borderRadius: 15,
    border: `1px solid ${theme.palette.divider}`,

    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.032)"
        : "#FFFFFF",

    boxShadow:
      theme.palette.type === "dark"
        ? "0 8px 22px rgba(0,0,0,.15)"
        : "0 7px 18px rgba(15,23,42,.05)",

    transition:
      "border-color .16s ease, box-shadow .16s ease, " +
      "background .16s ease, transform .16s ease",

    "&:hover": {
      borderColor:
        theme.palette.type === "dark"
          ? "rgba(96,165,250,.20)"
          : "rgba(21,94,239,.14)"
    },

    [theme.breakpoints.down("sm")]: {
      minWidth: "calc(100vw - 20px)",
      maxWidth: "calc(100vw - 20px)",
      borderRadius: 15
    }
  },
  columnHeader: {
    position: "sticky",
    top: 0,
    zIndex: 3,

    padding: "10px 10px 8px",

    borderRadius: "15px 15px 0 0",
    borderBottom: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(148,163,184,.10)"
        : "rgba(15,23,42,.055)"
    }`,

    background:
      theme.palette.type === "dark"
        ? "rgba(15,23,42,.94)"
        : "rgba(255,255,255,.95)",

    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)"
  },
  columnTitleRow: {
    minWidth: 0,
    marginBottom: 7,

    display: "flex",
    alignItems: "center",
    gap: 5
  },
  columnDot: {
    flexShrink: 0,
    width: 7,
    height: 7,
    marginRight: 1,
    borderRadius: "50%"
  },
  columnTitle: {
    minWidth: 0,
    flex: 1,

    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",

    color: theme.palette.text.primary,
    fontSize: ".80rem",
    fontWeight: 760,
    lineHeight: 1.2,
    letterSpacing: "-.012em"
  },
  columnCountBadge: {
    flexShrink: 0,
    minWidth: 22,
    padding: "2px 6px",

    textAlign: "center",
    borderRadius: 999,

    background:
      theme.palette.type === "dark"
        ? "rgba(148,163,184,.11)"
        : "rgba(15,23,42,.05)",

    color: theme.palette.text.secondary,

    fontSize: ".59rem",
    fontWeight: 700
  },
  columnProgress: {
    height: 4,
    marginBottom: 3,
    borderRadius: 999,
    backgroundColor: "rgba(15,23,42,.065)"
  },
  columnPercent: {
    color: theme.palette.text.secondary,
    fontSize: ".59rem",
    fontWeight: 650,
    lineHeight: 1,
    textAlign: "right"
  },
  columnCards: {
    flex: "0 0 auto",
    minHeight: 150,

    padding: "8px 7px 7px",
    overflowX: "hidden",
    overflowY: "visible",

    background:
      theme.palette.type === "dark"
        ? "rgba(2,6,23,.10)"
        : "rgba(248,250,252,.55)",

    ...theme.scrollbarStyles
  },
  columnFooter: {
    padding: "6px 10px",

    display: "flex",
    alignItems: "center",
    gap: 5,

    borderTop: `1px solid ${theme.palette.divider}`,
    borderRadius: "0 0 14px 14px",

    color: "#0066FF",
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.015)"
        : "#FFFFFF",

    fontSize: ".64rem",
    fontWeight: 700,

    cursor: "pointer",
    transition: "background .15s ease",

    "&:hover": {
      background: "rgba(0,102,255,.04)"
    }
  },
  card: {
    position: "relative",
    maxWidth: "100%",

    marginBottom: 7,
    padding: "9px",

    boxSizing: "border-box",
    borderRadius: 12,

    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.04)"
        : "#FFFFFF",

    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,.07)"
        : "1px solid rgba(15,23,42,.07)",

    boxShadow:
      theme.palette.type === "dark"
        ? "0 3px 9px rgba(0,0,0,.18)"
        : "0 2px 8px rgba(15,23,42,.045)",

    cursor: "grab",
    userSelect: "none",
    transition: "all .16s ease",

    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 11,
      bottom: 11,
      width: 2,
      borderRadius: "0 2px 2px 0",
      opacity: 0,
      background: "#0066FF",
      transition: "opacity .16s ease"
    },

    "&:hover": {
      transform: "translateY(-1px)",
      borderColor: "rgba(0,102,255,.18)",

      boxShadow:
        theme.palette.type === "dark"
          ? "0 8px 19px rgba(0,102,255,.12)"
          : "0 8px 20px rgba(0,102,255,.08)",

      "&::before": {
        opacity: 1
      }
    },

    "&:active": {
      cursor: "grabbing"
    }
  },
  cardHeader: {
    minWidth: 0,
    marginBottom: 2,

    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 4
  },
  cardName: {
    minWidth: 0,
    flex: 1,

    display: "-webkit-box",
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,

    color: theme.palette.text.primary,
    fontSize: ".75rem",
    fontWeight: 760,
    lineHeight: 1.22,
    letterSpacing: "-.008em"
  },
  cardTicket: {
    margin: "5px 0 4px",

    fontSize: ".60rem",
    fontWeight: 700,
    lineHeight: 1.2
  },
  cardInfo: {
    minWidth: 0,
    marginBottom: 3,

    display: "flex",
    alignItems: "center",
    gap: 4,

    color: theme.palette.text.secondary,
    fontSize: ".63rem",
    lineHeight: 1.22
  },
  cardFooter: {
    marginTop: 6,

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 4
  },
  cardQueue: {
    maxWidth: 128,
    height: 18,

    overflow: "hidden",
    borderRadius: 6,
    border: "1px solid rgba(0,102,255,.13)",

    background: "rgba(0,102,255,.06)",
    color: "#0066FF",

    fontSize: ".56rem",
    fontWeight: 680,

    "& .MuiChip-label": {
      overflow: "hidden",
      paddingLeft: 6,
      paddingRight: 6,
      textOverflow: "ellipsis"
    }
  },
  priorityChip: {
    flexShrink: 0,
    height: 18,
    borderRadius: 6,

    fontSize: ".56rem",
    fontWeight: 720,

    "& .MuiChip-label": {
      paddingLeft: 6,
      paddingRight: 6
    }
  },
  aiInsight: {
    flexShrink: 0,
    minWidth: 0,
    padding: "9px 11px",

    display: "flex",
    alignItems: "center",
    gap: 10,

    borderRadius: 13,
    border: "1px solid rgba(21,94,239,.12)",

    background:
      theme.palette.type === "dark"
        ? "rgba(21,94,239,.08)"
        : "linear-gradient(135deg,#FFFFFF,#F3F7FF)",

    boxShadow: "0 5px 16px rgba(15,23,42,.04)",

    [theme.breakpoints.down("sm")]: {
      padding: "10px",
      alignItems: "flex-start",
      flexWrap: "wrap"
    }
  },
  aiInsightIcon: {
    flexShrink: 0,
    width: 34,
    height: 34,

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: 10,
    color: "#FFFFFF",
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    boxShadow: "0 7px 16px rgba(0,102,255,.17)",

    fontSize: 16
  },
  aiInsightText: {
    minWidth: 160,
    flex: 1
  },
  aiInsightTitle: {
    color:
      theme.palette.type === "dark"
        ? "#FFFFFF"
        : "#101828",

    fontSize: ".82rem",
    fontWeight: 750,
    lineHeight: 1.25
  },
  aiInsightSub: {
    marginTop: 2,

    color:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.58)"
        : "#667085",

    fontSize: ".68rem",
    lineHeight: 1.35
  },
  cardAvatar: {
    flexShrink: 0,
    width: 32,
    height: 32,

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: 9,
    color: "#FFFFFF",
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    boxShadow: "0 5px 11px rgba(0,102,255,.13)",

    fontSize: 9.5,
    fontWeight: 800
  },
  cardNameRow: {
    minWidth: 0,
    flex: 1,

    display: "flex",
    alignItems: "center",
    gap: 8
  },
  kanbanNavHint: {
    minWidth: 0,
    padding: "7px 10px",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,

    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.035)"
        : "#FFFFFF",

    boxShadow: "0 4px 14px rgba(15,23,42,.035)",

    [theme.breakpoints.down("sm")]: {
      padding: "6px 8px"
    }
  },
  kanbanNavText: {
    minWidth: 0,
    flex: 1,

    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#475467",

    fontSize: ".67rem",
    fontWeight: 650,
    lineHeight: 1.3,

    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  },
  kanbanNavPill: {
    padding: "4px 8px",

    borderRadius: 999,
    border: "1px solid rgba(0,102,255,.13)",
    background: "rgba(0,102,255,.055)",
    color: "#0066FF",

    fontSize: ".61rem",
    fontWeight: 700
  },
  mobileScrollSpacer: {
    height: 24,
    flexShrink: 0
  },
  // FOLLOW-UP MODAL STYLES
  fuBtn: {
    flexShrink: 0,
    minHeight: 22,
    maxWidth: 82,
    padding: "2px 6px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,

    marginLeft: 1,
    overflow: "hidden",

    borderRadius: 7,
    border: "1px solid rgba(0,102,255,.13)",

    background: "rgba(0,102,255,.05)",
    color: "#0066FF",

    fontSize: ".55rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    cursor: "pointer",

    [theme.breakpoints.down("sm")]: {
      maxWidth: 72,
      padding: "2px 5px",
      fontSize: ".53rem"
    }
  },
  fuActiveBadge: {
    flexShrink: 0,
    width: 6,
    height: 6,
    marginRight: 2,

    display: "inline-block",
    borderRadius: "50%",

    background: "#00A86B",
    boxShadow: "0 0 5px rgba(0,168,107,.55)"
  },
  fuModal: {
    position: "fixed", inset: 0, zIndex: 1400,
    background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
  },
  fuModalBox: {
    background: theme.palette.type === "dark" ? "#0D1626" : "#fff",
    borderRadius: 20, width: "100%", maxWidth: 560,
    maxHeight: "90vh", overflow: "hidden",
    boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
    display: "flex", flexDirection: "column",
  },
  fuModalHeader: {
    padding: "16px 20px",
    background: "linear-gradient(135deg, #0044CC, #0066FF)",
    display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
  },
  fuModalBody: {
    flex: 1, overflowY: "auto", padding: "16px 20px",
    display: "flex", flexDirection: "column", gap: 14,
  },
  fuStep: {
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "#F8FAFC",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#E2E8F0"}`,
    borderRadius: 14, overflow: "hidden",
  },
  fuStepHeader: {
    padding: "10px 14px",
    background: "linear-gradient(135deg, rgba(0,102,255,0.06), rgba(0,102,255,0.02))",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  fuStepBody: { padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 },
  fuLabel: {
    fontWeight: 600, fontSize: "0.74rem",
    color: theme.palette.type === "dark" ? "#94A3B8" : "#475569",
    marginBottom: 4, display: "block",
  },
  fuInput: {
    width: "100%", padding: "8px 10px", borderRadius: 8,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#E2E8F0"}`,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#fff",
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#0A1628",
    fontSize: "0.82rem", outline: "none", boxSizing: "border-box", fontFamily: "inherit",
  },
  fuTextarea: {
    width: "100%", padding: "8px 10px", borderRadius: 8,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#E2E8F0"}`,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#fff",
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#0A1628",
    fontSize: "0.82rem", outline: "none", boxSizing: "border-box",
    fontFamily: "inherit", resize: "vertical", minHeight: 70, lineHeight: 1.5,
  },
  fuAddBtn: {
    width: "100%", padding: "10px", borderRadius: 10,
    border: "2px dashed rgba(0,102,255,0.25)",
    background: "rgba(0,102,255,0.02)", color: "#0066FF",
    cursor: "pointer", fontWeight: 700, fontSize: "0.8rem",
  },
  fuSaveBtn: {
    width: "100%", padding: "11px", borderRadius: 10,
    background: "linear-gradient(135deg, #0066FF, #00A3FF)",
    color: "#fff", border: "none", fontWeight: 700,
    fontSize: "0.86rem", cursor: "pointer",
    boxShadow: "0 4px 14px rgba(0,102,255,0.25)",
  },
  fuHint: {
    fontSize: "0.72rem", color: "#64748B", lineHeight: 1.5,
    background: "rgba(0,102,255,0.04)", border: "1px solid rgba(0,102,255,0.1)",
    borderRadius: 8, padding: "8px 12px",
  },
}));

const priorityColors = {
  high:   { label: "Alta",  bg: "rgba(255,77,77,0.1)",  color: "#FF4D4D", border: "rgba(255,77,77,0.2)" },
  medium: { label: "Media", bg: "rgba(255,200,87,0.1)", color: "#B8860B", border: "rgba(255,200,87,0.25)" },
  low:    { label: "Baixa", bg: "rgba(0,208,132,0.1)",  color: "#00A86B", border: "rgba(0,208,132,0.2)" },
};

const columnColors = [
  "#0066FF","#7C3AED","#0891B2","#059669","#DC2626",
  "#D97706","#DB2777","#9333EA","#2563EB","#16A34A","#EA580C","#0284C7",
];

const getPriority = (ticket) => {
  const tags = ticket.tags?.map(t => t.name?.toLowerCase()) || [];
  if (tags.some(t => t.includes("quente") || t.includes("alta"))) return "high";
  if (tags.some(t => t.includes("morno")  || t.includes("media"))) return "medium";
  return "low";
};

const formatPhone = (number) => {
  if (!number) return "";
  const n = number.replace(/\D/g, "");
  if (n.length === 13) return "(" + n.slice(2,4) + ") " + n.slice(4,9) + "-" + n.slice(9);
  if (n.length === 12) return "(" + n.slice(2,4) + ") " + n.slice(4,8) + "-" + n.slice(8);
  return number;
};

// ── Ghost global que segue o cursor ──────────────────────────────────────────
let ghostEl = null;
const createGhost = (sourceEl) => {
  if (ghostEl) ghostEl.remove();
  ghostEl = sourceEl.cloneNode(true);
  ghostEl.style.cssText = [
    "position:fixed","z-index:9999","pointer-events:none",
    "width:" + sourceEl.offsetWidth + "px",
    "opacity:0.85","transform:rotate(2deg) scale(1.03)",
    "box-shadow:0 12px 32px rgba(0,102,255,0.25)",
    "border-radius:12px","border:1.5px solid rgba(0,102,255,0.4)",
    "background:white","top:-9999px","left:-9999px",
  ].join(";");
  document.body.appendChild(ghostEl);
  return ghostEl;
};
const moveGhost = (x, y) => {
  if (!ghostEl) return;
  ghostEl.style.left = (x - ghostEl.offsetWidth / 2) + "px";
  ghostEl.style.top  = (y - 30) + "px";
};
const removeGhost = () => { if (ghostEl) { ghostEl.remove(); ghostEl = null; } };

const Kanban = () => {
  const classes  = useStyles();
  const theme = useTheme();
  const history  = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [tags,      setTags]      = useState([]);
  const [tickets,   setTickets]   = useState([]);
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate,   setEndDate]   = useState(format(new Date(), "yyyy-MM-dd"));
  const [dragOver,  setDragOver]  = useState(null);
  const [draggingId,setDraggingId]= useState(null);
  const [fuModal, setFuModal] = useState(null); // { tagId, tagName, color }
  const [fuSteps, setFuSteps] = useState([]);
  const [fuSaving, setFuSaving] = useState(false);
  const [fuMedias, setFuMedias] = useState([]);
  const [fuConfig, setFuConfig] = useState({}); // { tagId: [steps] }
  const [fuSettingsModal, setFuSettingsModal] = useState(false);
  const [fuSettings, setFuSettings] = useState({ maxPerRun: 20, delayMinSeconds: 3, delayMaxSeconds: 8, intervalMinutes: 5, active: true });
  const [fuSettingsSaving, setFuSettingsSaving] = useState(false);

  const drag = useRef(null); // { ticketId, sourceColId, active }
  const colRefs = useRef({});
  const boardScrollRef = useRef(null);
  const [boardScrollMax, setBoardScrollMax] = useState(0);
  const [boardScrollValue, setBoardScrollValue] = useState(0);

  const queueIds = user.queues.map((q) => q.UserQueue.queueId);

  useEffect(() => { fetchTags(); loadFuSettings(); }, [user]);

  const fetchTags = async () => {
    try {
      const { data } = await api.get("/tag/kanban/");
      const lista = data.lista || [];
      setTags(lista);
      loadFuConfig(lista);
      fetchTickets();
    } catch (e) { console.log(e); }
  };

  const fetchTickets = async () => {
    try {
      const { data } = await api.get("/ticket/kanban", {
        params: { queueIds: JSON.stringify(queueIds), startDate, endDate },
      });
      setTickets(data.tickets);
    } catch (e) { setTickets([]); }
  };

  const openFuModal = async (tag) => {
    setFuModal({ tagId: tag.id, tagName: tag.title, color: tag.color });
    try {
      const { data } = await api.get("/kanban-followup/" + tag.id);
      setFuSteps(data.length > 0 ? data : [{ delayHours: 2, message: "", mediaKey: "", mediaName: "", mediaType: "", nextTagId: "" }]);
    } catch (e) { setFuSteps([{ delayHours: 2, message: "", mediaKey: "", mediaName: "", mediaType: "", nextTagId: "" }]); }
    try {
      const { data } = await api.get("/cavia-medias");
      setFuMedias(data || []);
    } catch (e) { setFuMedias([]); }
  };

  const closeFuModal = () => { setFuModal(null); setFuSteps([]); };

  const addFuStep = () => {
    setFuSteps(prev => [...prev, { delayHours: 24, message: "", mediaKey: "", mediaName: "", mediaType: "", nextTagId: "" }]);
  };

  const removeFuStep = (idx) => {
    setFuSteps(prev => prev.filter((_, i) => i !== idx));
  };

  const updateFuStep = (idx, field, value) => {
    setFuSteps(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const saveFuSteps = async () => {
    if (!fuModal) return;
    setFuSaving(true);
    try {
      await api.post("/kanban-followup/" + fuModal.tagId + "/batch", { steps: fuSteps });
      const newConfig = { ...fuConfig, [fuModal.tagId]: fuSteps };
      setFuConfig(newConfig);
      toast.success("Follow-up salvo com sucesso!");
      closeFuModal();
    } catch (e) { toast.error("Erro ao salvar follow-up"); }
    setFuSaving(false);
  };

  const loadFuConfig = async (tagsList) => {
    try {
      const configs = {};
      await Promise.all(tagsList.map(async (tag) => {
        const { data } = await api.get("/kanban-followup/" + tag.id);
        if (data.length > 0) configs[tag.id] = data;
      }));
      setFuConfig(configs);
    } catch (e) {}
  };

  const loadFuSettings = async () => {
    try {
      const { data } = await api.get("/kanban-followup-settings");
      setFuSettings(data);
    } catch (e) {}
  };

  const saveFuSettings = async () => {
    setFuSettingsSaving(true);
    try {
      await api.post("/kanban-followup-settings", fuSettings);
      toast.success("Configurações salvas!");
      setFuSettingsModal(false);
    } catch (e) { toast.error("Erro ao salvar configurações"); }
    setFuSettingsSaving(false);
  };

  useEffect(() => {
    const companyId = user.companyId;
    const onMsg = (data) => {
      if (["create","update","delete"].includes(data.action)) fetchTickets();
    };
    socket.on("company-" + companyId + "-ticket", onMsg);
    socket.on("company-" + companyId + "-appMessage", onMsg);
    return () => {
      socket.off("company-" + companyId + "-ticket", onMsg);
      socket.off("company-" + companyId + "-appMessage", onMsg);
    };
  }, [socket, startDate, endDate]);

  useEffect(() => {
    const board = boardScrollRef.current;

    if (!board) return undefined;

    const updateMetrics = () => {
      const max = Math.max(
        board.scrollWidth - board.clientWidth,
        0
      );

      setBoardScrollMax(max);
      setBoardScrollValue(
        Math.min(board.scrollLeft, max)
      );
    };

    const handleBoardScroll = () => {
      setBoardScrollValue(board.scrollLeft);
    };

    updateMetrics();

    const timer = window.setTimeout(
      updateMetrics,
      250
    );

    board.addEventListener(
      "scroll",
      handleBoardScroll,
      { passive: true }
    );

    window.addEventListener(
      "resize",
      updateMetrics
    );

    return () => {
      window.clearTimeout(timer);

      board.removeEventListener(
        "scroll",
        handleBoardScroll
      );

      window.removeEventListener(
        "resize",
        updateMetrics
      );
    };
  }, [tags.length, tickets.length]);

  const handleBoardNavigator = event => {
    const value = Number(event.target.value);

    setBoardScrollValue(value);

    if (boardScrollRef.current) {
      boardScrollRef.current.scrollLeft = value;
    }
  };

  // ── Detecta em qual coluna o cursor está ────────────────────────────────────
  const getColAtPoint = useCallback((x, y) => {
    for (const [colId, el] of Object.entries(colRefs.current)) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return colId;
    }
    return null;
  }, []);

  // ── Mouse handlers ──────────────────────────────────────────────────────────
  const startDrag = useCallback((ticket, sourceColId, sourceEl, startX, startY) => {
    drag.current = { ticketId: ticket.id, sourceColId, active: false, startX, startY };
    const onMove = (clientX, clientY) => {
      const dx = Math.abs(clientX - drag.current.startX);
      const dy = Math.abs(clientY - drag.current.startY);
      if (!drag.current.active && (dx > 5 || dy > 5)) {
        drag.current.active = true;
        setDraggingId(ticket.id);
        createGhost(sourceEl);
        document.body.style.cursor = "grabbing";
        document.body.style.userSelect = "none";
      }
      if (drag.current.active) {
        moveGhost(clientX, clientY);
        const colId = getColAtPoint(clientX, clientY);
        setDragOver(colId);
      }
    };
    const onEnd = async (clientX, clientY) => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      removeGhost();
      if (!drag.current || !drag.current.active) {
        history.push("/tickets/" + ticket.uuid);
        drag.current = null;
        setDraggingId(null);
        setDragOver(null);
        return;
      }
      const targetColId = getColAtPoint(clientX, clientY);
      const { ticketId, sourceColId: srcId } = drag.current;
      drag.current = null;
      setDraggingId(null);
      setDragOver(null);
      if (!targetColId || targetColId === srcId) return;
      setTickets(prev => prev.map(t => {
        if (t.id !== ticketId) return t;
        const filtered = t.tags.filter(tag => tag.kanban !== 1);
        if (targetColId !== "lane0") {
          const targetTag = tags.find(tg => tg.id.toString() === targetColId);
          if (targetTag) filtered.push(targetTag);
        }
        return { ...t, tags: filtered };
      }));
      try {
        await api.delete("/ticket-tags/" + ticketId);
        if (targetColId !== "lane0") {
          await api.put("/ticket-tags/" + ticketId + "/" + targetColId);
        }
        toast.success("Ticket movido!");
      } catch (err) {
        toast.error("Erro ao mover ticket");
        fetchTickets();
      }
    };
    return { onMove, onEnd };
  }, [tags, getColAtPoint, history]);

  const handleMouseDown = useCallback((e, ticket, sourceColId) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const sourceEl = e.currentTarget;
    const { onMove, onEnd } = startDrag(ticket, sourceColId, sourceEl, e.clientX, e.clientY);
    const onMouseMove = (ev) => onMove(ev.clientX, ev.clientY);
    const onMouseUp = (ev) => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      onEnd(ev.clientX, ev.clientY);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [startDrag]);

  const handleTouchStart = useCallback((e, ticket, sourceColId) => {
    const touch = e.touches[0];
    const sourceEl = e.currentTarget;
    const { onMove, onEnd } = startDrag(ticket, sourceColId, sourceEl, touch.clientX, touch.clientY);
    const onTouchMove = (ev) => {
      if (drag.current && drag.current.active) ev.preventDefault();
      const t = ev.touches[0];
      onMove(t.clientX, t.clientY);
    };
    const onTouchEnd = (ev) => {
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      const t = ev.changedTouches[0];
      onEnd(t.clientX, t.clientY);
    };
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd);
  }, [startDrag]);

  // ── Colunas ─────────────────────────────────────────────────────────────────
  const noTagTickets = tickets.filter(t => t.tags.length === 0);
  const columns = [
    { id: "lane0", title: i18n.t("tagsKanban.laneDefault"), color: "#0066FF", tickets: noTagTickets },
    ...tags.map((tag, idx) => ({
      id: tag.id.toString(), title: tag.name,
      color: tag.color || columnColors[idx % columnColors.length],
      tickets: tickets.filter(t => t.tags.map(i => i.id).includes(tag.id)),
    })),
  ];

  const totalTickets = tickets.length;
  const wonTickets = tickets.filter(t =>
    t.tags.some(tag => tag.name?.toLowerCase().includes("ganho") || tag.name?.toLowerCase().includes("fechado"))
  ).length;
  const negTickets = tickets.filter(t =>
    t.tags.some(tag => tag.name?.toLowerCase().includes("qualif") || tag.name?.toLowerCase().includes("negoc"))
  ).length;

  return (
    <KoraxPage>

      <KoraxPageHeader
        title="CRM"
        subtitle="Acompanhe seus leads, negociações e conversões em um único pipeline comercial."
        actions={
          <>
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <KoraxButton
                  variant="secondary"
                  startIcon={<AddIcon />}
                  onClick={() => history.push("/tagsKanban")}
                >
                  Colunas
                </KoraxButton>
              )}
            />

            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <KoraxButton
                  variant="primary"
                  startIcon={<FlashOnIcon />}
                  onClick={() => setFuSettingsModal(true)}
                >
                  Follow-up
                </KoraxButton>
              )}
            />
          </>
        }
      />

      <KoraxStatsGrid>
        <KoraxStatCard
          label="Leads ativos"
          value={totalTickets}
          hint="Total no período"
          badge={`${columns.length} etapas`}
          color="#155EEF"
          icon={<PeopleAltIcon />}
        />

        <KoraxStatCard
          label="Em negociação"
          value={negTickets}
          hint="Leads em andamento"
          badge="Pipeline aberto"
          color="#7C3AED"
          icon={<TrendingUpIcon />}
        />

        <KoraxStatCard
          label="Fechados"
          value={wonTickets}
          hint="Negócios concluídos"
          badge="Resultado comercial"
          color="#16A34A"
          icon={<CheckCircleOutlineIcon />}
        />

        <KoraxStatCard
          label="Conversão"
          value={`${totalTickets > 0
            ? Math.round((wonTickets / totalTickets) * 100)
            : 0}%`}
          hint="Fechados sobre o total"
          badge="No período"
          color="#D97706"
          icon={<ShowChartIcon />}
        />
      </KoraxStatsGrid>

      <Paper elevation={0} className={classes.filterBar}>
        <div className={classes.filterRow}>
          <TextField label="Data inicio" type="date" value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }} variant="outlined" size="small"
            className={classes.dateField} style={{ minWidth: 155 }} />
          <TextField label="Data fim" type="date" value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }} variant="outlined" size="small"
            className={classes.dateField} style={{ minWidth: 155 }} />
          <Button variant="contained" onClick={fetchTickets} className={classes.searchBtn} startIcon={<SearchIcon />}>
            Buscar
          </Button>
        </div>
        <div className={classes.filterInfo}>
          <Chip label={columns.length + " etapas ativas"} className={classes.infoChip} size="small" />
          <Chip label={noTagTickets.length + " sem etapa"}  className={classes.infoChip} size="small" />
          <Chip label={totalTickets + " tickets no período"} className={classes.infoChip} size="small" />
        </div>
      </Paper>

      <div className={classes.boardNavigator}>
        <div className={classes.boardNavigatorHeader}>
          <span className={classes.boardNavigatorTitle}>
            Navegar pelo pipeline
          </span>

          <span className={classes.boardNavigatorMeta}>
            {columns.length} etapas
          </span>
        </div>

        <div className={classes.boardNavigatorControl}>
          <span className={classes.boardNavigatorArrow}>
            ‹
          </span>

          <input
            type="range"
            min="0"
            max={boardScrollMax || 0}
            step="1"
            value={Math.min(
              boardScrollValue,
              boardScrollMax
            )}
            onChange={handleBoardNavigator}
            className={classes.boardNavigatorRange}
            aria-label="Mover Kanban horizontalmente"
          />

          <span className={classes.boardNavigatorArrow}>
            ›
          </span>
        </div>
      </div>

      <div className={classes.boardShell}>
        <div
          ref={boardScrollRef}
          className={classes.boardScroll}
        >
          {columns.map((col) => {
            const pct    = totalTickets > 0 ? Math.round((col.tickets.length / totalTickets) * 100) : 0;
            const isOver = dragOver === col.id;
            const colStyle = isOver ? {
              borderColor: "rgba(0,102,255,.48)",
              boxShadow:
                "0 0 0 2px rgba(0,102,255,.12), " +
                "0 12px 28px rgba(0,102,255,.10)",
              background:
                theme.palette.type === "dark"
                  ? "rgba(0,102,255,.08)"
                  : "#F6F9FF",
              transform: "translateY(-2px)"
            } : {};
            return (
              <div
                key={col.id}
                className={classes.column}
                style={colStyle}
                ref={el => { colRefs.current[col.id] = el; }}
              >
                <div className={classes.columnHeader}>
                  <div className={classes.columnTitleRow}>
                    <div className={classes.columnDot} style={{ background: col.color }} />
                    <Typography className={classes.columnTitle}>{col.title}</Typography>
                    <span className={classes.columnCountBadge}>{col.tickets.length}</span>
                    {col.id !== "lane0" && (
                      <button className={classes.fuBtn}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => { e.stopPropagation(); openFuModal(col); }}>
                        {fuConfig[col.id] && fuConfig[col.id].length > 0
                          ? <><span className={classes.fuActiveBadge}/></>
                          : null}
                        ⚡ Follow-up
                      </button>
                    )}
                  </div>
                  <LinearProgress variant="determinate" value={pct} className={classes.columnProgress} />
                  <Typography className={classes.columnPercent} style={{ color: col.color }}>{pct}%</Typography>
                </div>

                <div className={classes.columnCards}>
                  {isOver && draggingId !== null && (
                    <div style={{
                      borderRadius: 10, border: "2px dashed rgba(0,102,255,0.4)",
                      background: "rgba(0,102,255,0.05)", height: 68, marginBottom: 10,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "rgba(0,102,255,0.6)", fontSize: "0.75rem", fontWeight: 600,
                    }}>
                      Soltar aqui
                    </div>
                  )}
                  {col.tickets.map((ticket) => {
                    const priority       = getPriority(ticket);
                    const pConfig        = priorityColors[priority];
                    const phone          = formatPhone(ticket.contact?.number);
                    const isDraggingThis = draggingId === ticket.id;
                    return (
                      <div
                        key={ticket.id}
                        className={classes.card}
                        style={isDraggingThis ? { opacity: 0.35, transform: "scale(0.97)" } : {}}
                        onMouseDown={(e) => handleMouseDown(e, ticket, col.id)}
                        onTouchStart={(e) => handleTouchStart(e, ticket, col.id)}
                      >
                        <div className={classes.cardHeader}>
                          <div className={classes.cardNameRow}>
                            <div className={classes.cardAvatar}>
                              {(ticket.contact?.name || "S").trim().slice(0, 2).toUpperCase()}
                            </div>
                            <Typography className={classes.cardName}>{ticket.contact?.name || "Sem nome"}</Typography>
                          </div>
                          <IconButton size="small" style={{ padding: 2 }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}>
                            <MoreHorizIcon style={{ fontSize: 15, color: "#94a3b8" }} />
                          </IconButton>
                        </div>
                        <Typography className={classes.cardTicket} style={{ color: col.color }}>
                          {"Ticket #" + ticket.id}
                        </Typography>
                        {phone && (
                          <div className={classes.cardInfo}>
                            <PhoneIcon style={{ fontSize: 12 }} />
                            <span>{phone}</span>
                          </div>
                        )}
                        {ticket.lastMessage && (
                          <div className={classes.cardInfo}>
                            <WhatsAppIcon style={{ fontSize: 12, color: "#25d366" }} />
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%", minWidth: 0, flex: 1 }}>
                              {ticket.lastMessage}
                            </span>
                          </div>
                        )}
                        <div className={classes.cardFooter}>
                          <Chip label={ticket.queue?.name || "Comercial"} className={classes.cardQueue} size="small" />
                          <Chip label={pConfig.label} size="small" className={classes.priorityChip}
                            style={{ background: pConfig.bg, color: pConfig.color, border: "1px solid " + pConfig.border }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className={classes.columnFooter} onClick={() => history.push("/tickets")}>
                  <AddIcon style={{ fontSize: 13 }} />
                  <span>Adicionar card</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={classes.mobileScrollSpacer} />

      {/* FOLLOW-UP MODAL */}
      {fuModal && (
        <div className={classes.fuModal} onClick={e => e.target === e.currentTarget && closeFuModal()}>
          <div className={classes.fuModalBox}>
            <div className={classes.fuModalHeader}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem" }}>⚡</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: "0.9rem", color: "#fff" }}>Follow-up automático</div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.7)", marginTop: 1 }}>{fuModal.tagName}</div>
              </div>
              <button onClick={closeFuModal} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: 7, width: 28, height: 28, cursor: "pointer", fontSize: "1.1rem" }}>×</button>
            </div>
            <div className={classes.fuModalBody}>
              <div className={classes.fuHint}>
                💡 Use <strong>{"{{nome}}"}</strong> para personalizar com o nome do lead. A sequência dispara automaticamente após os tempos configurados.
              </div>
              {fuSteps.map((step, idx) => (
                <div key={idx} className={classes.fuStep}>
                  <div className={classes.fuStepHeader}>
                    <div style={{ fontWeight: 700, fontSize: "0.8rem", color: fuModal.color }}>Passo {idx + 1}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: "0.72rem", color: "#64748B" }}>após</span>
                      <input type="number" min="0.5" step="0.5" value={step.delayHours}
                        onChange={e => updateFuStep(idx, "delayHours", parseFloat(e.target.value))}
                        style={{ width: 60, padding: "3px 6px", borderRadius: 6, border: "1px solid #E2E8F0", fontSize: "0.78rem", outline: "none", textAlign: "center" }} />
                      <span style={{ fontSize: "0.72rem", color: "#64748B" }}>horas</span>
                      {fuSteps.length > 1 && (
                        <IconButton size="small" onClick={() => removeFuStep(idx)} style={{ color: "#EF4444", padding: 2 }}>
                          <DeleteOutlineIcon style={{ fontSize: 15 }} />
                        </IconButton>
                      )}
                    </div>
                  </div>
                  <div className={classes.fuStepBody}>
                    <div>
                      <label className={classes.fuLabel}>💬 Mensagem (opcional)</label>
                      <textarea className={classes.fuTextarea} value={step.message}
                        onChange={e => updateFuStep(idx, "message", e.target.value)}
                        placeholder={"Oi {{nome}}, tudo bem? Passando para ver se posso te ajudar..."} />
                    </div>
                    <div>
                      <label className={classes.fuLabel}>🎵 Áudio (opcional)</label>
                      <select style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: "0.82rem", outline: "none", background: "#F8FAFC" }}
                        value={step.mediaKey}
                        onChange={e => {
                          const m = fuMedias.find(m => m.mediaKey === e.target.value);
                          updateFuStep(idx, "mediaKey", e.target.value);
                          updateFuStep(idx, "mediaName", m ? m.name : "");
                          updateFuStep(idx, "mediaType", m ? m.type : "");
                        }}>
                        <option value="">Sem áudio</option>
                        {fuMedias.filter(m => m.type === "audio").map(m => (
                          <option key={m.id} value={m.mediaKey}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={classes.fuLabel}>➡️ Avançar para coluna (último passo)</label>
                      <select style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: "0.82rem", outline: "none", background: "#F8FAFC" }}
                        value={step.nextTagId || ""}
                        onChange={e => updateFuStep(idx, "nextTagId", e.target.value)}>
                        <option value="">Manter na coluna atual</option>
                        {tags.filter(t => t.id.toString() !== fuModal.tagId.toString()).map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
              <button className={classes.fuAddBtn} onClick={addFuStep}>+ Adicionar passo</button>
              <button className={classes.fuSaveBtn} onClick={saveFuSteps} disabled={fuSaving}>
                {fuSaving ? "Salvando..." : "💾 Salvar follow-up"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* FOLLOW-UP SETTINGS MODAL */}
      {fuSettingsModal && (
        <div className={classes.fuModal} onClick={e => e.target === e.currentTarget && setFuSettingsModal(false)}>
          <div className={classes.fuModalBox} style={{ maxWidth: 480 }}>
            <div className={classes.fuModalHeader}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem" }}>⚙️</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: "0.9rem", color: "#fff" }}>Configurações de Follow-up</div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.7)", marginTop: 1 }}>Controle o ritmo de envio automático</div>
              </div>
              <button onClick={() => setFuSettingsModal(false)} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: 7, width: 28, height: 28, cursor: "pointer", fontSize: "1.1rem" }}>×</button>
            </div>
            <div className={classes.fuModalBody}>
              <div className={classes.fuHint}>
                ⚠️ Configure com cuidado. Envios muito rápidos podem causar bloqueio no WhatsApp.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <label className={classes.fuLabel}>📦 Máx. envios por rodada</label>
                  <input type="number" min="1" max="100" className={classes.fuInput}
                    value={fuSettings.maxPerRun}
                    onChange={e => setFuSettings(s => ({ ...s, maxPerRun: parseInt(e.target.value) }))} />
                  <div style={{ fontSize: "0.68rem", color: "#94A3B8", marginTop: 3 }}>Recomendado: 10-20</div>
                </div>
                <div>
                  <label className={classes.fuLabel}>🔄 Intervalo do job (minutos)</label>
                  <input type="number" min="1" max="60" className={classes.fuInput}
                    value={fuSettings.intervalMinutes}
                    onChange={e => setFuSettings(s => ({ ...s, intervalMinutes: parseInt(e.target.value) }))} />
                  <div style={{ fontSize: "0.68rem", color: "#94A3B8", marginTop: 3 }}>Recomendado: 5 min</div>
                </div>
                <div>
                  <label className={classes.fuLabel}>⏱️ Delay mínimo (segundos)</label>
                  <input type="number" min="1" max="60" className={classes.fuInput}
                    value={fuSettings.delayMinSeconds}
                    onChange={e => setFuSettings(s => ({ ...s, delayMinSeconds: parseInt(e.target.value) }))} />
                  <div style={{ fontSize: "0.68rem", color: "#94A3B8", marginTop: 3 }}>Entre cada mensagem</div>
                </div>
                <div>
                  <label className={classes.fuLabel}>⏱️ Delay máximo (segundos)</label>
                  <input type="number" min="1" max="120" className={classes.fuInput}
                    value={fuSettings.delayMaxSeconds}
                    onChange={e => setFuSettings(s => ({ ...s, delayMaxSeconds: parseInt(e.target.value) }))} />
                  <div style={{ fontSize: "0.68rem", color: "#94A3B8", marginTop: 3 }}>Entre cada mensagem</div>
                </div>
              </div>
              <div style={{ background: "rgba(0,102,255,0.04)", border: "1px solid rgba(0,102,255,0.1)", borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ fontWeight: 700, fontSize: "0.78rem", color: "#0066FF", marginBottom: 6 }}>📊 Simulação de envio</div>
                <div style={{ fontSize: "0.76rem", color: "#475569", lineHeight: 1.7 }}>
                  A cada <strong>{fuSettings.intervalMinutes} min</strong>, o sistema envia até <strong>{fuSettings.maxPerRun} mensagens</strong> com intervalo de <strong>{fuSettings.delayMinSeconds}-{fuSettings.delayMaxSeconds}s</strong> entre cada uma.
                  <br/>Tempo total por rodada: ~<strong>{Math.round(fuSettings.maxPerRun * ((fuSettings.delayMinSeconds + fuSettings.delayMaxSeconds) / 2))}s</strong>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, background: fuSettings.active ? "rgba(0,168,107,0.06)" : "rgba(239,68,68,0.06)", border: `1px solid ${fuSettings.active ? "rgba(0,168,107,0.2)" : "rgba(239,68,68,0.2)"}` }}>
                <span style={{ fontSize: "0.8rem", fontWeight: 600, color: fuSettings.active ? "#00A86B" : "#EF4444", flex: 1 }}>
                  {fuSettings.active ? "✅ Follow-up automático ATIVO" : "⏸️ Follow-up automático PAUSADO"}
                </span>
                <button onClick={() => setFuSettings(s => ({ ...s, active: !s.active }))}
                  style={{ padding: "5px 14px", borderRadius: 8, border: "none", fontWeight: 700, fontSize: "0.76rem", cursor: "pointer", background: fuSettings.active ? "rgba(239,68,68,0.1)" : "rgba(0,168,107,0.1)", color: fuSettings.active ? "#EF4444" : "#00A86B" }}>
                  {fuSettings.active ? "Pausar" : "Ativar"}
                </button>
              </div>
              <button className={classes.fuSaveBtn} onClick={saveFuSettings} disabled={fuSettingsSaving}>
                {fuSettingsSaving ? "Salvando..." : "💾 Salvar configurações"}
              </button>
            </div>
          </div>
        </div>
      )}
    </KoraxPage>
  );
};

export default Kanban;
